import { Download, Pencil, RefreshCw } from 'lucide-react';
import type { MouseEvent } from 'react';
import { PreviewDoc } from '@/components/job-detail/PreviewDoc';
import { AsyncActionBtn } from '@/components/ui/AsyncActionBtn';
import { Button } from '@/components/ui/Button';

const contentHash = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(36);
};

export interface ResumeOrCoverTabProps {
  applicationId: number | null;
  company: string;
  kind: 'resume' | 'cover';
  markdown: string | null;
  onRetailor: () => Promise<unknown>;
  pdfPath: string | null;
  subtitle?: string;
}

export const ResumeOrCoverTab = ({
  applicationId,
  company,
  kind,
  markdown,
  onRetailor,
  pdfPath,
  subtitle,
}: ResumeOrCoverTabProps) => {
  const docTitle = kind === 'resume' ? 'resume' : 'cover letter';
  const pdfUrl =
    pdfPath && applicationId != null
      ? `/api/applications/${applicationId}/${
          kind === 'resume' ? 'resume.pdf' : 'cover-letter.pdf'
        }?v=${contentHash(markdown ?? '')}`
      : null;

  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <div className="space-y-3 h-[calc(100%-var(--status-bar-height))]">
      <div className="flex items-center justify-between">
        <p className="font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
          {kind === 'resume' ? 'Tailored for' : 'Cover letter for'}{' '}
          <span className="text-foreground">{company}</span>
          {subtitle ? ` · ${subtitle}` : null}
        </p>
        <div className="flex gap-1.5">
          {kind === 'resume' ? (
            <AsyncActionBtn
              icon={RefreshCw}
              onAction={onRetailor}
              size="xs"
              variant="soft"
            >
              Retailor
            </AsyncActionBtn>
          ) : (
            <Button onClick={stop} size="xs" variant="outline">
              <Pencil className="size-3" /> Edit
            </Button>
          )}
          {pdfUrl && (
            <a
              className="inline-flex h-6 items-center gap-1 rounded-md border border-accent bg-accent px-2 text-xs lg:text-sm font-medium text-white shadow-sm hover:bg-accent/90"
              download
              href={pdfUrl}
              onClick={stop}
            >
              <Download className="size-3" /> Download PDF
            </a>
          )}
        </div>
      </div>
      {pdfUrl ? (
        <iframe
          className="min-h-200 h-full w-full rounded-md border border-border bg-white"
          src={pdfUrl}
          title={docTitle}
        />
      ) : markdown ? (
        <>
          <p className="text-2xs lg:text-xs text-subtle">
            PDF preview unavailable — showing the markdown version.
          </p>
          <PreviewDoc
            body={markdown}
            fontClassName={kind === 'resume' ? 'font-resume' : undefined}
            title={docTitle}
          />
        </>
      ) : (
        <p className="text-xs lg:text-base text-foreground-muted">
          No {docTitle} available.
        </p>
      )}
    </div>
  );
};
